import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { useFocusHeadingOnNavigate } from '../ui/focus';
import { MoodPicker } from './MoodPicker';
import { TopicCards } from './TopicCards';
import { ConcernTags } from './ConcernTags';
import { SafetyQuestion } from './SafetyQuestion';
import { SwipeCard } from './SwipeCard';

type Step = 'mood' | 'topics' | 'concern' | 'safety';

interface Props {
  /** The context line + "you said, we did" quote — shown only on the first card, same as a single-card check-in shows it once. */
  intro?: ReactNode;
  mood: number | null;
  onMood: (v: number) => void;
  topics: string[];
  onTopics: (v: string[]) => void;
  allowedTopics?: readonly string[] | null;
  concernTags: string[];
  onConcernTags: (v: string[]) => void;
  concernPhrases: string[];
  /** 0026, spec §14.10: whether this session offers the closing Safety Valve question. */
  safetyValve?: boolean;
  wantsCounsellor: boolean | null;
  onWantsCounsellor: (v: boolean) => void;
  onDone: () => void;
  submitting: boolean;
  error: string | null;
}

// Release 4b's student card deck: one card per step (mood → topics →
// concern, the last only while "Something else" is ticked), extending the
// same stepper pattern as the admin "Add a school" wizard — a step heading
// that takes focus on change, Back/Next buttons — rather than any
// touch-gesture carousel. MoodPicker/TopicTags are reused completely
// unchanged; this component only decides which one is mounted. 0026 adds an
// optional closing 'safety' step, same conditional-step treatment as
// 'concern': present in `steps` only when the server says this session
// offers it, and never before the end of the deck.
export function CheckInDeck({
  intro, mood, onMood, topics, onTopics, allowedTopics,
  concernTags, onConcernTags, concernPhrases,
  safetyValve, wantsCounsellor, onWantsCounsellor,
  onDone, submitting, error,
}: Props) {
  const { t } = useI18n();
  const steps = useMemo<Step[]>(() => {
    const base: Step[] = topics.includes('other') ? ['mood', 'topics', 'concern'] : ['mood', 'topics'];
    return safetyValve ? [...base, 'safety'] : base;
  }, [topics, safetyValve]);
  const [step, setStep] = useState<Step>('mood');
  const idx = Math.max(0, steps.indexOf(step));
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);

  // On in-app navigation TO this page (not a fresh load), the deck's own
  // heading is the page's h1 now (the frame no longer owns one), so it
  // takes the same first-mount focus CheckinFrame's h1 used to.
  useFocusHeadingOnNavigate(stepHeading);

  // Move focus to the new step's heading when the step changes (not on the
  // first load, where the page heading already has it) — same rule as the
  // admin wizard's step focus.
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepHeading.current?.focus();
  }, [step]);

  // Unticking "Something else" while the concern card is showing removes it
  // from the deck; fall back a step instead of leaving the deck pointed at a
  // card that no longer exists.
  useEffect(() => {
    if (!steps.includes(step)) setStep(steps[steps.length - 1]);
  }, [steps, step]);

  function next() {
    if (idx < steps.length - 1) setStep(steps[idx + 1]);
    else onDone();
  }
  // TopicCards' last card calls onChange(finalTopics) and this in the same
  // tick — `topics` (and so `steps`) here is still last render's value, so
  // deciding from it would miss "other" just now being added. Decide from
  // the array TopicCards actually has, not this render's stale `steps`.
  function finishTopics(finalTopics: string[]) {
    if (finalTopics.includes('other')) setStep('concern');
    else if (safetyValve) setStep('safety');
    else onDone();
  }
  function back() {
    if (idx > 0) setStep(steps[idx - 1]);
  }

  const canNext = (step !== 'mood' || mood !== null) && (step !== 'safety' || wantsCounsellor !== null);
  const stepHeadingText =
    step === 'mood' ? t['ci.title'] : step === 'topics' ? t['ci.topicsTitle'] : step === 'concern' ? t['r4b.ci.concernTitle'] : t['r6.ci.safetyTitle'];
  // §6: directions are logical (forward = towards the inline end of the
  // reading direction), so Arabic's right-to-left page mirrors which
  // physical side commits — set once per render, it only ever changes on a
  // language switch, which remounts this page anyway.
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

  return (
    <div className="sx-deck">
      <ol className="sx-stepper" aria-hidden="true">
        {steps.map((s, i) => (
          <li key={s} className={i === idx ? 'on' : i < idx ? 'done' : ''} />
        ))}
      </ol>
      <p className="visually-hidden" role="status">{t['r4b.ci.stepOf'](idx + 1, steps.length)}</p>

      {step !== 'topics' && (
        <h1 className="sx-title" id="ci-deck-title" ref={stepHeading} tabIndex={-1}>
          {stepHeadingText}
        </h1>
      )}

      {idx === 0 && intro}

      {step === 'mood' && (
        <SwipeCard
          onCommit={(dir) => {
            if (dir === 'forward') next();
          }}
          canCommit={(dir) => dir === 'forward' && mood !== null}
          rtl={rtl}
        >
          <p className="sx-hint" id="ci-anon">{t['ci.anon']}</p>
          <p className="visually-hidden" id="ci-scale-hint">{t['ci.scaleHint']}</p>
          <MoodPicker value={mood} onChange={onMood} labelledBy="ci-deck-title" describedBy="ci-scale-hint ci-anon" />
        </SwipeCard>
      )}
      {step === 'topics' && (
        <TopicCards value={topics} onChange={onTopics} allowed={allowedTopics} rtl={rtl} onBackToMood={back} onAllDone={finishTopics} />
      )}
      {step === 'concern' && (
        <div className="sx-section">
          <p className="sx-hint" id="ci-concern-hint">{t['r4b.ci.concernHint']}</p>
          <ConcernTags value={concernTags} onChange={onConcernTags} labelledBy="ci-deck-title" describedBy="ci-concern-hint" allowed={concernPhrases} />
        </div>
      )}
      {step === 'safety' && (
        <div className="sx-section">
          <p className="sx-hint" id="ci-safety-hint">{t['r6.ci.safetyHint']}</p>
          <SafetyQuestion value={wantsCounsellor} onChange={onWantsCounsellor} labelledBy="ci-deck-title" describedBy="ci-safety-hint" />
        </div>
      )}

      {step !== 'topics' && (
        <div className="sx-actions">
          {error && <p className="sx-error" role="alert">{error}</p>}
          <div className="sx-deck-nav">
            {idx > 0 && (
              <button type="button" className="sx-btn-ghost" onClick={back} disabled={submitting}>
                {t['r4b.ci.back']}
              </button>
            )}
            <button
              type="button"
              className="sx-btn"
              disabled={!canNext || submitting}
              aria-describedby={!canNext ? 'ci-pick-first' : undefined}
              onClick={next}
            >
              {submitting ? t['ci.sending'] : idx === steps.length - 1 ? t['ci.done'] : t['r4b.ci.next']}
            </button>
          </div>
          {!canNext && <p className="sx-hint sx-center" id="ci-pick-first">{step === 'safety' ? t['r6.ci.safetyPickFirst'] : t['ci.pickFirst']}</p>}
        </div>
      )}
      {step === 'topics' && error && (
        <p className="sx-error" role="alert">{error}</p>
      )}
      {step === 'topics' && submitting && (
        <p className="sx-hint sx-center" role="status">{t['ci.sending']}</p>
      )}
    </div>
  );
}
